import * as React from 'react';
import { Nullable, OrgComponentProps } from '../typescriptHelpers';
import FormItem from 'antd/lib/form/FormItem';
import InputNumber from 'antd/lib/input-number';
import Text from 'antd/lib/typography/Text';
import Checkbox from 'antd/lib/checkbox';
import { RelativeTiming, LifecycleEventType, ReleaseScheduleCadenceStyle, RelativeTimeTrigger } from '../types';
import Select from 'antd/lib/select';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { selectScheduleSettings } from '../selectors/selectors';
import { useWatch } from 'antd/lib/form/Form';
import { Form } from 'antd/lib';
import { runwayColors } from './styles/globalStyles';
import { useRelativeTimeTriggerOptions } from '@/utils/hooks/useRelativeTimeTriggerOptions';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';

const MULTI_SELECTOR_KEY = 'lifecycleEventType+waitForAllDestinations';

export const DEFAULT_RELATIVE_TIME_TRIGGER: RelativeTimeTrigger = {
    enabled: false,
    relativeDays: 0,
    relativeHours: 0,
    relativeMinutes: 0,
    timing: RelativeTiming.onTime,
    lifecycleEventType: LifecycleEventType.LifecycleEventTypeKickoff,
    waitForAllDestinations: false,
    allowTriggeringBasedOnTargetDate: false,
};

interface RelativeTimeTriggerSettingsProps extends OrgComponentProps {
    appId?: string;
    stepConfigId: Nullable<string>;
    initialValues: Partial<RelativeTimeTrigger>;
    parentFieldName?: string | (string | number)[];
    prefixPath?: (string | number)[];
    isOrgLevelItem?: boolean;
    showAllowTriggeringBasedOnTargetDate?: boolean;
    label?: string;
    lifecycleEventTypes?: LifecycleEventType[];
    isBeforeTimingHidden?: boolean;
}

export const RelativeTimeTriggerSettings: React.FC<RelativeTimeTriggerSettingsProps> = ({
    appId,
    stepConfigId,
    initialValues,
    parentFieldName = 'relativeTimeTrigger',
    prefixPath = [],
    isOrgLevelItem,
    showAllowTriggeringBasedOnTargetDate = false,
    label,
    lifecycleEventTypes,
    isBeforeTimingHidden = false,
}: RelativeTimeTriggerSettingsProps) => {
    const form = Form.useFormInstance();
    const fieldPath = React.useMemo(
        () => (Array.isArray(parentFieldName) ? parentFieldName : [parentFieldName]),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(parentFieldName)],
    );
    const absoluteFieldPath = React.useMemo(
        () => [...prefixPath, ...fieldPath],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(prefixPath), fieldPath],
    );
    const selectedLifecycleEventType = useWatch<LifecycleEventType>([...absoluteFieldPath, 'lifecycleEventType']);
    const selectedTiming = useWatch<RelativeTiming>([...absoluteFieldPath, 'timing']);
    const multiSelectorValue = useWatch<string>([...absoluteFieldPath, MULTI_SELECTOR_KEY]);
    const scheduleSettings = useSelector((state: RootState) => (appId ? selectScheduleSettings(state, appId) : null));
    const hasActiveSchedule =
        scheduleSettings ? scheduleSettings.scheduleCadence !== ReleaseScheduleCadenceStyle.Never : false;

    const shouldDisableBeforeOptionForRelativeTiming = React.useMemo(() => {
        if (isOrgLevelItem) return false;
        if (!hasActiveSchedule || !selectedLifecycleEventType) return true;

        const targetKickoff = scheduleSettings?.cadenceTargetDateSets?.[0]?.kickoffDayOfWeek;
        const targetSubmit = scheduleSettings?.cadenceTargetDateSets?.[0]?.submitDayOfWeek;
        const targetRelease = scheduleSettings?.cadenceTargetDateSets?.[0]?.releaseDayOfWeek;

        switch (selectedLifecycleEventType) {
            case LifecycleEventType.LifecycleEventTypeKickoff:
                return !targetKickoff;
            case LifecycleEventType.LifecycleEventTypeSubmit:
                return !targetSubmit;
            case LifecycleEventType.LifecycleEventTypeRelease:
                return !targetRelease;
            default:
                return false;
        }
    }, [isOrgLevelItem, hasActiveSchedule, scheduleSettings, selectedLifecycleEventType]);

    const {
        lifecycleEventOptions,
        relativeTimingOptions,
        parseSelectedLifecycleEventOption,
        convertToSelectedLifecycleEventValue,
    } = useRelativeTimeTriggerOptions(appId, stepConfigId);
    const visibleLifecycleEventOptions =
        lifecycleEventTypes ?
            lifecycleEventOptions.filter((option) =>
                lifecycleEventTypes.includes(parseSelectedLifecycleEventOption(option.value).lifecycleEventType),
            )
        :   lifecycleEventOptions;
    const visibleRelativeTimingOptions =
        isBeforeTimingHidden ?
            relativeTimingOptions.filter((option) => option.value !== RelativeTiming.before)
        :   relativeTimingOptions;

    const stableInitialValues = React.useMemo(
        () => initialValues,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            initialValues.allowTriggeringBasedOnTargetDate,
            initialValues.enabled,
            initialValues.lifecycleEventType,
            initialValues.relativeDays,
            initialValues.relativeHours,
            initialValues.relativeMinutes,
            initialValues.timing,
            initialValues.waitForAllDestinations,
        ],
    );

    React.useEffect(() => {
        const trigger: RelativeTimeTrigger & { [MULTI_SELECTOR_KEY]?: string } = {
            ...DEFAULT_RELATIVE_TIME_TRIGGER,
            ...stableInitialValues,
        };

        const initialSelectedEventOption = convertToSelectedLifecycleEventValue(
            trigger.lifecycleEventType,
            trigger.waitForAllDestinations,
        );

        trigger[MULTI_SELECTOR_KEY] = initialSelectedEventOption;
        const currentEnabled = form.getFieldValue([...absoluteFieldPath, 'enabled']);
        if (currentEnabled !== undefined) {
            trigger.enabled = currentEnabled;
        }
        form.setFieldValue(absoluteFieldPath, trigger);
    }, [form, absoluteFieldPath, stableInitialValues, convertToSelectedLifecycleEventValue]);

    React.useEffect(() => {
        if (selectedTiming === RelativeTiming.before && shouldDisableBeforeOptionForRelativeTiming) {
            form.setFieldValue([...absoluteFieldPath, 'timing'], RelativeTiming.after);
        } else if (selectedTiming === RelativeTiming.onTime) {
            form.setFieldValue([...absoluteFieldPath, 'relativeDays'], 0);
            form.setFieldValue([...absoluteFieldPath, 'relativeHours'], 0);
            form.setFieldValue([...absoluteFieldPath, 'relativeMinutes'], 0);
        }
    }, [form, absoluteFieldPath, selectedTiming, shouldDisableBeforeOptionForRelativeTiming]);

    React.useEffect(() => {
        if (multiSelectorValue) {
            const parsedValue = parseSelectedLifecycleEventOption(multiSelectorValue);
            form.setFieldValue([...absoluteFieldPath, 'lifecycleEventType'], parsedValue.lifecycleEventType);
            form.setFieldValue([...absoluteFieldPath, 'waitForAllDestinations'], parsedValue.waitForAllDestinations);
        }
    }, [form, absoluteFieldPath, multiSelectorValue, parseSelectedLifecycleEventOption]);

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    columnGap: 12,
                    alignItems: 'center',
                    marginBottom: 0,
                    width: '100%',
                }}
            >
                {label && <Text style={{ whiteSpace: 'nowrap' }}>{label}</Text>}
                <FormItem name={[...fieldPath, 'relativeDays']} style={{ marginBottom: 0, marginTop: 0 }}>
                    <InputNumber
                        min={0}
                        disabled={selectedTiming === RelativeTiming.onTime}
                        style={timeInputStyle}
                        suffix={<Text type="secondary">d</Text>}
                    />
                </FormItem>
                <FormItem name={[...fieldPath, 'relativeHours']} style={{ marginBottom: 0, marginTop: 0 }}>
                    <InputNumber
                        min={0}
                        max={23}
                        disabled={selectedTiming === RelativeTiming.onTime}
                        style={timeInputStyle}
                        suffix={<Text type="secondary">h</Text>}
                    />
                </FormItem>
                <FormItem name={[...fieldPath, 'relativeMinutes']} style={{ marginBottom: 0, marginTop: 0 }}>
                    <InputNumber
                        min={0}
                        max={59}
                        disabled={selectedTiming === RelativeTiming.onTime}
                        style={timeInputStyle}
                        suffix={<Text type="secondary">m</Text>}
                    />
                </FormItem>
                <FormItem
                    name={[...fieldPath, 'timing']}
                    style={{
                        marginBottom: 0,
                        minWidth: label ? 90 : 120,
                        flex: 1,
                    }}
                >
                    <Select
                        options={visibleRelativeTimingOptions.map((option) => ({
                            ...option,
                            disabled:
                                option.value === RelativeTiming.before ?
                                    shouldDisableBeforeOptionForRelativeTiming
                                :   false,
                        }))}
                    />
                </FormItem>
                <FormItem
                    name={[...fieldPath, MULTI_SELECTOR_KEY]}
                    style={{ minWidth: label ? 90 : 120, flex: 1, marginBottom: 0, marginTop: 0 }}
                >
                    <Select options={visibleLifecycleEventOptions} />
                </FormItem>
                <FormItem name={[...fieldPath, 'lifecycleEventType']} noStyle style={{ width: 0, height: 0 }} />
                <FormItem name={[...fieldPath, 'waitForAllDestinations']} noStyle style={{ width: 0, height: 0 }} />
            </div>

            {shouldDisableBeforeOptionForRelativeTiming && !isBeforeTimingHidden && (
                <div className="ant-form-item-explain" style={{ color: runwayColors.statusGrey }}>
                    <ul
                        style={{
                            paddingLeft: 16,
                            fontSize: '12px',
                            paddingBottom: 0,
                            marginBottom: 0,
                            marginTop: 10,
                            color: runwayColors.statusGrey,
                        }}
                    >
                        <li>
                            Setting target times before lifecycle events requires a schedule cadence to be set in
                            Schedule settings.
                        </li>
                    </ul>
                </div>
            )}
            {showAllowTriggeringBasedOnTargetDate && selectedTiming !== RelativeTiming.before && (
                <FormItem
                    name={[...fieldPath, 'allowTriggeringBasedOnTargetDate']}
                    valuePropName="checked"
                    style={{ marginBottom: 0, marginTop: 12 }}
                >
                    <Checkbox>
                        Allow triggering based on target date
                        <TooltipInfoIcon
                            tooltipContent="If configured to trigger on or after an event, allow triggering based on the target date even if the event hasn't occurred yet"
                            style={{ marginLeft: 8 }}
                        />
                    </Checkbox>
                </FormItem>
            )}
            {isOrgLevelItem && (
                <div className="ant-form-item-explain" style={{ color: runwayColors.statusGrey }}>
                    <ul
                        style={{
                            paddingLeft: 16,
                            fontSize: '12px',
                            paddingBottom: 0,
                            marginBottom: 0,
                            marginTop: 10,
                            color: runwayColors.statusGrey,
                        }}
                    >
                        <li>
                            Setting target times relative to a lifecycle event will only work if the lifecycle event is
                            configured to exist for each applicable step.
                        </li>
                        {selectedTiming === RelativeTiming.before && (
                            <li>
                                Setting target times before lifecycle events requires a schedule cadence to be set in
                                every app's Schedule settings.
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

const timeInputStyle: React.CSSProperties = {
    width: 70,
    textAlign: 'center',
    borderRadius: 8,
};
